import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { SelectField } from "@/components/SelectField/SelectField";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import {
	AvailableSender,
	CampaignAction,
	CampaignPending,
	MigrationLevelEnum,
} from "../schema/pendingSchedules";

import { MigrationLevelSelector } from "./MigrationLevelSelector";
import { ForcedMigrationConfirmDialog } from "./ForcedMigrationConfirmDialog";

const CampaignRowSchema = z.object({
	campaignId: z.string(),
	listId: z.number(),
	action: z.enum(["cancel", "reassign", "skip"]),
	newUserId: z.number().optional(),
	newEmailId: z.number().optional(),
	addToCampaign: z.boolean().optional(),
});

const FormSchema = z.object({
	rows: z.array(CampaignRowSchema),
	migrationLevel: MigrationLevelEnum,
});

type FormValues = z.infer<typeof FormSchema>;

interface PendingSchedulesDialogProps {
	isOpen: boolean;
	totalCount: number;
	campaigns: CampaignPending[];
	senderType: "email" | "linkedin" | "user";
	isLoading: boolean;
	onConfirm: (campaignActions: CampaignAction[]) => void;
	onClose: () => void;
	zIndex?: number;
}

export const PendingSchedulesDialog: FunctionComponent<PendingSchedulesDialogProps> = ({
	isOpen,
	totalCount,
	campaigns,
	senderType,
	isLoading,
	onConfirm,
	onClose,
	zIndex,
}) => {
	const { t } = useTranslation();
	const [showForcedConfirm, setShowForcedConfirm] = useState(false);

	const { control, watch, setValue, handleSubmit, reset } = useForm<FormValues>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			rows: campaigns.map((c) => ({
				campaignId: c.campaignId,
				listId: c.listId,
				action: "cancel" as const,
				addToCampaign: false,
			})),
			migrationLevel: "safe",
		},
	});

	useFieldArray({ control, name: "rows" });

	useEffect(() => {
		reset({
			rows: campaigns.map((c) => ({
				campaignId: c.campaignId,
				listId: c.listId,
				action: "cancel" as const,
				addToCampaign: false,
			})),
			migrationLevel: "safe",
		});
	}, [campaigns, reset]);

	const rows = watch("rows");
	const hasAnyReassign = rows.some((row) => row.action === "reassign");

	useEffect(() => {
		if (!hasAnyReassign) {
			setValue("migrationLevel", "safe");
		}
	}, [hasAnyReassign, setValue]);

	const isFormValid = rows.every((row) => {
		if (!row.action) {
			return false;
		}

		if (row.action === "reassign" && !row.newUserId) {
			return false;
		}

		return true;
	});

	const getRowType = useCallback(
		(campaign: CampaignPending): "email" | "linkedin" | "user" => {
			return campaign.originSender?.type ?? senderType;
		},
		[senderType]
	);

	const filterSenders = useCallback(
		(senders: AvailableSender[], type: "email" | "linkedin" | "user") => {
			if (type === "user") {
				return senders;
			}

			return senders.filter((s) => s.type === type);
		},
		[]
	);

	const getSenderKey = useCallback(
		(sender: AvailableSender, type: "email" | "linkedin" | "user") => {
			if (type === "user") {
				return String(sender.userId);
			}

			return `${sender.userId}-${sender.emailId ?? "li"}`;
		},
		[]
	);

	const buildCampaignActions = useCallback((values: FormValues): CampaignAction[] => {
		return values.rows.map((row) => ({
			campaignId: row.campaignId,
			listId: row.listId,
			action: row.action,
			migrationLevel: values.migrationLevel,
			...(row.action === "reassign" && row.newUserId ? { newUserId: row.newUserId } : {}),
			...(row.action === "reassign" && row.newEmailId ? { newEmailId: row.newEmailId } : {}),
			...(row.action === "reassign" && row.addToCampaign !== undefined
				? { addToCampaign: row.addToCampaign }
				: {}),
		}));
	}, []);

	const onSubmit = (values: FormValues) => {
		if (values.migrationLevel === "forced") {
			setShowForcedConfirm(true);

			return;
		}

		onConfirm(buildCampaignActions(values));
	};

	const handleForcedConfirm = useCallback(() => {
		setShowForcedConfirm(false);
		const values = watch();

		onConfirm(buildCampaignActions(values));
	}, [buildCampaignActions, onConfirm, watch]);

	const getSenderLabel = useCallback(
		(sender: AvailableSender, type: "email" | "linkedin" | "user") => {
			if (type === "email" && sender.emailAddress) {
				return `${sender.name} (${sender.emailAddress})`;
			}

			return sender.name;
		},
		[]
	);

	const actionOptions = useCallback(
		(campaign: CampaignPending) => {
			const rowType = getRowType(campaign);
			const filtered = filterSenders(campaign.availableSenders, rowType);
			const opts = [
				{ title: t("pendingSchedules.cancel"), value: "cancel" },
				{ title: t("pendingSchedules.doNothing"), value: "skip" },
			];

			if (filtered.length > 0) {
				opts.push({ title: t("pendingSchedules.reassign"), value: "reassign" });
			}

			return opts;
		},
		[filterSenders, getRowType, t]
	);

	const getSenderOptions = useCallback(
		(campaign: CampaignPending) => {
			const rowType = getRowType(campaign);
			const filtered = filterSenders(campaign.availableSenders, rowType);
			const inCampaign = filtered.filter((s) => s.isInCampaign);
			const notInCampaign = filtered.filter((s) => !s.isInCampaign);

			return [
				...inCampaign.map((s) => ({
					title: `${getSenderLabel(s, rowType)} ✓`,
					value: getSenderKey(s, rowType),
				})),
				...notInCampaign.map((s) => ({
					title: getSenderLabel(s, rowType),
					value: getSenderKey(s, rowType),
				})),
			];
		},
		[filterSenders, getSenderLabel, getSenderKey, getRowType]
	);

	const handleSenderSelect = (
		index: number,
		value: string | number | null,
		campaign: CampaignPending
	) => {
		if (!value) {
			return;
		}

		const rowType = getRowType(campaign);
		const strValue = String(value);

		if (rowType === "user") {
			const userId = parseInt(strValue, 10);

			setValue(`rows.${index}.newUserId`, userId);
			setValue(`rows.${index}.newEmailId`, undefined);
		} else {
			const parts = strValue.split("-");
			const userId = parseInt(parts[0], 10);
			const emailIdPart = parts[1];

			setValue(`rows.${index}.newUserId`, userId);
			if (rowType === "email" && emailIdPart !== "li") {
				setValue(`rows.${index}.newEmailId`, parseInt(emailIdPart, 10));
			}
		}

		const filtered = filterSenders(campaign.availableSenders, rowType);
		const sender = filtered.find((s) => getSenderKey(s, rowType) === strValue);

		setValue(`rows.${index}.addToCampaign`, sender ? !sender.isInCampaign : false);
	};

	const getSelectedSenderValue = (row: FormValues["rows"][number], campaign: CampaignPending) => {
		if (!row.newUserId) {
			return null;
		}

		const rowType = getRowType(campaign);

		if (rowType === "user") {
			return String(row.newUserId);
		}

		return `${row.newUserId}-${row.newEmailId ?? "li"}`;
	};

	return (
		<>
			<Modal
				disableSave={!isFormValid}
				handleClose={onClose}
				handleSave={handleSubmit(onSubmit)}
				isLoading={isLoading}
				isOpened={isOpen}
				overflow
				size="lg"
				submitButtonColor={ButtonColor.DANGER}
				submitButtonText={t("pendingSchedules.deleteSenderAndApply")}
				title={t("pendingSchedules.title")}
				zIndex={zIndex}
			>
				<div className="flex flex-col gap-4">
					<div className="text-text text-sm">
						{t("pendingSchedules.description", {
							count: totalCount,
							campaignCount: campaigns.length,
						})}
					</div>

					<div className="border border-border-light rounded-lg max-h-[50vh] overflow-y-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b border-border-light">
									<th className="text-left text-text p-3 font-medium bg-brand-white rounded-tl-lg">
										{t("pendingSchedules.fromSender")}
									</th>
									<th className="text-left text-text p-3 font-medium bg-brand-white">
										{t("pendingSchedules.campaign")}
									</th>
									<th className="text-left text-text p-3 font-medium bg-brand-white">
										{t("pendingSchedules.list")}
									</th>
									<th className="text-left text-text p-3 font-medium bg-brand-white">
										{t("pendingSchedules.pending")}
									</th>
									<th className="text-left text-text p-3 font-medium bg-brand-white">
										{t("pendingSchedules.action")}
									</th>
									<th className="text-left text-text p-3 font-medium bg-brand-white rounded-tr-lg">
										{t("pendingSchedules.sender")}
									</th>
								</tr>
							</thead>
							<tbody>
								{campaigns.map((campaign, index) => {
									const row = rows[index];
									const rowType = getRowType(campaign);
									const selectedSender =
										row?.action === "reassign"
											? campaign.availableSenders.find(
													(s) =>
														s.userId === row.newUserId &&
														(rowType !== "email" || s.emailId === row.newEmailId)
												)
											: null;
									const showAddToCampaign =
										row?.action === "reassign" && selectedSender && !selectedSender.isInCampaign;

									return (
										<tr
											key={campaign.campaignId}
											className="border-b border-border-light last:border-b-0"
										>
											<td className="p-3 text-text">
												{campaign.originSender ? (
													<div className="flex flex-col">
														<span className="font-medium">{campaign.originSender.name}</span>
														{campaign.originSender.type === "email" &&
															campaign.originSender.emailAddress && (
																<span className="text-text-meta text-xs">
																	{campaign.originSender.emailAddress}
																</span>
															)}
														{campaign.originSender.type === "linkedin" && (
															<span className="text-text-meta text-xs">LinkedIn</span>
														)}
													</div>
												) : (
													<span className="text-text-meta">—</span>
												)}
											</td>
											<td className="p-3 text-text">{campaign.campaignName}</td>
											<td className="p-3 text-text-meta">{campaign.listName}</td>
											<td className="p-3 text-text-meta">{campaign.pendingCount}</td>
											<td className="p-3 min-w-[140px]">
												<SelectField
													handleSelect={(value) => {
														setValue(
															`rows.${index}.action`,
															value as "cancel" | "reassign" | "skip"
														);
														if (value === "cancel" || value === "skip") {
															setValue(`rows.${index}.newUserId`, undefined);
															setValue(`rows.${index}.newEmailId`, undefined);
															setValue(`rows.${index}.addToCampaign`, false);
														}
													}}
													hideEmptyValue
													options={actionOptions(campaign)}
													selectedOption={row?.action ?? null}
												/>
											</td>
											<td className="p-3 min-w-[200px]">
												{row?.action === "reassign" && (
													<div className="flex flex-col gap-2">
														<SelectField
															handleSelect={(value) => handleSenderSelect(index, value, campaign)}
															hideEmptyValue
															options={getSenderOptions(campaign)}
															selectedOption={getSelectedSenderValue(row, campaign)}
														/>
														{showAddToCampaign && (
															<Checkbox
																isChecked={row.addToCampaign ?? true}
																onChange={() =>
																	setValue(`rows.${index}.addToCampaign`, !row.addToCampaign)
																}
															>
																<span className="text-text-meta text-xs">
																	{t("pendingSchedules.addToCampaign")}
																</span>
															</Checkbox>
														)}
													</div>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					{hasAnyReassign && (
						<MigrationLevelSelector
							value={watch("migrationLevel")}
							onChange={(level) => setValue("migrationLevel", level)}
						/>
					)}
				</div>
			</Modal>
			<ForcedMigrationConfirmDialog
				isOpen={showForcedConfirm}
				zIndex={zIndex ? zIndex + 1 : undefined}
				onCancel={() => setShowForcedConfirm(false)}
				onConfirm={handleForcedConfirm}
			/>
		</>
	);
};
